import React, { useState, useEffect } from "react";
import { Box, IconButton, Paper, Drawer, Tooltip } from "@mui/material";
import {
  DoubleArrow,
  Edit,
  FileCopy,
  PowerSettingsNew,
  MotionPhotosAuto,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { tooltipStyleProps } from "services/constants/Styles";
import { themes } from "services/constants";
import { customGetAPI } from "services/utils/CustomAPI";
import { Loader } from "components/Loader";
import { useForm } from "react-hook-form";
import { Preview } from "../Component/Preview";
import { EditShiftCategory } from "../EditShiftCategory";
import { CloneShiftCategory } from "../CloneShiftCategory";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";

const timeBoundBreakDeductFromInternalValues = {
  Start: "start_time",
  Mid: "mid_time",
  End: "end_time",
};

const mapTimeBoundBreakRulesFromApi = (rows) =>
  Array.isArray(rows)
    ? rows.map((row) => ({
        hours: row?.threshold_hours ?? 0,
        minutes: row?.threshold_minutes ?? 0,
        deduct_minutes: row?.deduct_minutes ?? 0,
        deduct_from:
          timeBoundBreakDeductFromInternalValues[row?.deduct_zone] ||
          "start_time",
      }))
    : [];

export const ViewSftCategory = ({
  onClose,
  editCategoryDetails,
  reCallApi,
  setHandleLoader,
  screenAccess,
  complianceData,
  onTaskStart,
  onTaskUpdate,
  onTaskComplete,
  setIsViewPanelOpen,
}) => {
  const { setValue, watch } = useForm({
    defaultValues: {
      loading: false,
    },
  });

  const isEmpty = (obj) => !obj || Object.keys(obj).length === 0;
  const { domain } = useSelector((state) => state.tokenReducer);
  const [openSidePeak, setOpenSidePeak] = useState(false);
  const [openCloneSft, setOpenCloneSft] = useState(false);
  const [getViewScreenData, setViewScreenData] = useState(true);
  const [getData, setGetData] = useState([]);
  const [formData, setFormData] = useState({
    basic: {},
    adjustment: {},
    earning: {},
    deductions: {},
  });

  const handleEditShift = () => {
    setOpenSidePeak(true);
  };

  const handleEditClosePanel = () => {
    setOpenSidePeak(false);
  };

  const handleCloneShiftPanel = () => {
    setOpenSidePeak(true);
    setOpenCloneSft(true);
  };

  const handleCloseClonePanel = () => {
    setOpenSidePeak(false);
    setOpenCloneSft(false);
  };

  useEffect(() => {
    const getSftCategoryData = async () => {
      setValue("loading", true);
      const res = await customGetAPI(
        `${domain}administration/shiftroster/shiftrota/shift-categories?shift_category_id=${editCategoryDetails?.id}`,
      );
      if (res.ok) {
        setGetData(res?.responseDetails[0]);
        setValue("loading", false);
      }
    };
    if (getViewScreenData && editCategoryDetails?.id) {
      setViewScreenData(false);
      getSftCategoryData();
    }
  }, [
    domain,
    editCategoryDetails?.id,
    editCategoryDetails?.name,
    getViewScreenData,
    setValue,
    watch,
  ]);

  useEffect(() => {
    if (getData?.length !== 0) {
      const basicCompensation = (
        getData?.compensation_rules?.[0]?.shift_percentage_ranges || [
          { work_min_percentage: "", count_as_shift: "" },
        ]
      ).map(({ work_min_percentage = "", count_as_shift = "" } = {}) => ({
        work_min_percentage,
        count_as_shift,
      }));

      const earnigCompensation = (
        getData?.overtime_policies?.[0]?.overtime_compensation_rules[0]
          ?.ot_percentage_ranges || [
          { work_min_percentage: "", count_as_shift: "" },
        ]
      ).map(({ work_min_percentage = "", count_as_shift = "" } = {}) => ({
        work_min_percentage,
        count_as_shift,
      }));

      const holidayCompensation = (
        getData?.holiday_category[0]?.holiday_compensation_rules[0]
          ?.holiday_percentage_ranges || [
          { work_min_percentage: "", count_as_shift: "" },
        ]
      ).map(({ work_min_percentage = "", count_as_shift = "" } = {}) => ({
        work_min_percentage,
        count_as_shift,
      }));

      const weekoffCompensation = (
        getData?.weekoff_category[0]?.weekoff_compensation_rules[0]
          ?.weekoff_percentage_ranges || [
          { work_min_percentage: "", count_as_shift: "" },
        ]
      ).map(({ work_min_percentage = "", count_as_shift = "" } = {}) => ({
        work_min_percentage,
        count_as_shift,
      }));

      setFormData({
        basic: {
          sft_category_name: getData?.name,
          payment_based_on: getData?.payment_based_on ?? "Full Shift Credit",
          rows: basicCompensation,
          additional_percentage:
            getData?.compensation_rules?.[0]?.additional_percentage ?? 0,
          shift_count: getData?.compensation_rules?.[0]?.shift_per_day ?? 0,
          is_include_sft_grade_sfts:
            getData?.other_shift_grade_shifts_choice ?? "Shift Hours",
          is_manual_break_allowed: getData?.is_manual_break_allowed ?? false,
          is_auto_deduct_break: getData?.is_auto_deduct_break ?? false,
          manual_break_duration:
            getData?.break_application_policy?.manual_break_duration || 30,
          is_fixed_deduction:
            getData?.break_application_policy?.is_fixed_deduction ?? false,
          fixed_deduction_type:
            getData?.break_application_policy?.fixed_deduction_type ||
            "custom",
          fixed_deduction_custom_duration:
            getData?.break_application_policy?.fixed_deduction_custom_duration ||
            30,
          is_deduct_missed_breaks:
            getData?.break_application_policy?.is_deduct_missed_breaks ?? false,
          missed_break_deduction_type:
            getData?.break_application_policy?.missed_break_deduction_type ||
            "custom",
          missed_break_custom_duration:
            getData?.break_application_policy?.missed_break_custom_duration ||
            30,
          is_deduct_short_breaks:
            getData?.break_application_policy?.is_deduct_short_breaks ?? false,
          short_break_deduction_type:
            getData?.break_application_policy?.short_break_deduction_type ||
            "custom",
          short_break_custom_duration:
            getData?.break_application_policy?.short_break_custom_duration ||
            20,
          break_deduction_applicability_percentage:
            getData?.break_application_policy
              ?.break_deduction_applicability_percentage || 75,
          break_deduction_applicability_type:
            getData?.break_application_policy?.deduction_applicability ||
            "Percentage",
          is_break_time_bound_break_deduction_allowed:
            getData?.is_time_bount_break_deductions ??
            getData?.break_application_policy?.is_time_bount_break_deductions ??
            false,
          break_time_bound_deduction_configurations:
            mapTimeBoundBreakRulesFromApi(
              getData?.shift_time_boundary_rules ||
                getData?.break_application_policy?.shift_time_boundary_rules,
            ),
          sftCategoryName: editCategoryDetails?.name,
          sftCategoryId: editCategoryDetails?.id,
        },
        //Adjustment
        adjustment: {
          is_onduty_allowed: getData?.is_onduty_allowed,
          is_permission_allowed: getData?.is_permission_allowed,
          is_permission_include_working_hour:
            getData?.is_permission_include_working_hour,
          is_permission_hours_included_in_the_priority_deduction:
            getData?.is_permission_hours_included_in_the_priority_deduction,
          is_compoff_allowed: getData?.is_compoff_allowed,
          is_weekoff_sandwich_leave:
            getData?.is_weekoff_sandwich_leave ?? false,
          absence_rule_type:
            getData?.is_weekoff_sandwich_leave_data?.absence_rule_type ??
            "Before",
          is_holiday_sandwich_leave:
            getData?.is_holiday_sandwich_leave ?? false,
          absence_rule_type_for_holiday:
            getData?.is_holiday_sandwich_leave_data?.absence_rule_type ??
            "Before",
          is_work_adjustment_priority:
            getData?.is_work_adjustment_priority ?? false,
          absence_work_adjustment_priority_bands:
            getData?.work_adjustment_priorities?.map((band, index) => ({
              band_id: band.band_id,
              band_name: band.band_name ?? "",
              factor: band.band_factor ?? "",
              band_order: band.band_order ?? index + 1,
              priorities: Array.isArray(band.penalty_assignments)
                ? band.penalty_assignments.map((penalty) => ({
                    name: penalty.penalty_type,
                    type:
                      penalty.penalty_type === "Break Penalty"
                        ? "Break"
                        : penalty.penalty_type === "Absence Penalty"
                          ? "Absent"
                          : penalty.penalty_type === "Shift Penalty"
                            ? "Shift"
                            : penalty.penalty_type === "Permission Penalty"
                              ? "Permission"
                              : "",
                    priority: penalty.deduction_order,
                  }))
                : [],
            })) ?? [],
        },
        earning: {
          is_weekoff_paid: getData?.is_paid_week_off,
          as_per_week_day_for_week_off: getData?.as_per_week_day_for_week_off,
          as_per_week_day_for_holiday: getData?.as_per_week_day_for_holiday,
          is_holiday_paid: getData?.is_paid_holiday,
          is_absence_paid: getData?.is_absence_paid,
          is_overtime_allowed: getData?.ot_allowed,
          //ot
          is_ot_payment_based_on:
            getData?.overtime_policies[0]?.payment_based_on ?? "Attendance",
          ot_timing_privilege:
            getData?.overtime_policies[0]?.ot_time_privilage ?? "Only Before",
          ot_work_preauthorization:
            getData?.overtime_policies[0]?.ot_work_pre_authorization ??
            "Automatic",
          calculation_based_on:
            getData?.overtime_policies[0]?.calculation_based_on ?? "Hours",
          ot_attendance_payment_factor:
            getData?.overtime_policies[0]?.calculation_based_on === "Hours" &&
            getData?.overtime_policies[0]?.payment_based_on === "Attendance"
              ? getData?.overtime_policies?.[0]?.payment_factor
              : 0,
          ot_sft_payment_factor:
            getData?.overtime_policies[0]?.calculation_based_on === "Shift"
              ? getData?.overtime_policies?.[0]?.payment_factor
              : 0,
          is_ot_pay_by_default:
            getData?.overtime_policies?.[0]?.added_payroll_default ?? 0,
          fixed_amount_deduct_type:
            getData?.overtime_policies?.[0]?.payment_based_on === "Amount"
              ? getData?.overtime_policies?.[0]?.consideration
              : "",
          attendance_deduct_type:
            getData?.overtime_policies?.[0]?.payment_based_on === "Attendance"
              ? getData?.overtime_policies?.[0]?.consideration
              : "",
          minimum_ot_consideration:
            getData?.overtime_policies?.[0]?.minimum_ot_consideration_minutes ??
            0,
          additional_ot:
            getData?.overtime_policies?.[0]
              ?.additional_ot_consideration_minutes ?? 0,
          minutes_for_consideration:
            getData?.overtime_policies?.[0]?.minutes_for_consideration ?? 0,
          additional_minutes_for_consideration:
            getData?.overtime_policies?.[0]
              ?.additional_minutes_for_consideration ?? 0,
          ot_payment_factor_configurations:
            getData?.overtime_policies?.[0]?.payment_factor_configurations ??
            getData?.overtime_policies?.[0]?.work_bands ??
            [],
          is_allow_payment_factor_for_ot:
            getData?.overtime_policies?.[0]?.attendance_payment_factor ?? false,
          is_add_remaining_ot_to_base_factor:
            getData?.overtime_policies?.[0]?.add_remaining_minutes_to_payroll ??
            false,
          ot_equivalent_hours_configurations:
            getData?.overtime_policies?.[0]?.equivalent_hours ?? [],
          equivalent_hours_additional_minutes:
            getData?.overtime_policies?.[0]
              ?.equivalent_hours_additional_minutes ?? 0,
          equivalent_hours_ot_minutes:
            getData?.overtime_policies?.[0]?.equivalent_hours_ot_minutes ?? 0,
          ot_fixed_amount_thereshold_limit:
            getData?.overtime_policies?.[0]
              ?.threshold_ot_consideration_minutes ?? 0,
          ot_fixed_amount_payable:
            getData?.overtime_policies?.[0]?.ot_amount_payable ?? 0,
          ot_fixed_amount_additional_mins:
            getData?.overtime_policies?.[0]
              ?.additional_threshold_ot_consideration_minutes ?? 0,
          ot_fixed_amount_additional_payable:
            getData?.overtime_policies?.[0]?.additional_ot_amount_payable ?? 0,
          rows: earnigCompensation,
          deduct_additional_percentage:
            getData?.overtime_policies?.[0]?.overtime_compensation_rules?.[0]
              ?.additional_percentage ?? 0,

          deduct_shift_count:
            getData?.overtime_policies?.[0]?.overtime_compensation_rules?.[0]
              ?.shift_per_day ?? 0,
          weekoffRows: weekoffCompensation,
          holidayRows: holidayCompensation,
          //weekoff
          is_payment_for_weekoff_day_attendance:
            getData?.weekoff_category?.payment_for_week_off_attendance ?? false,
          weekoff_attendance_fixed_amount:
            getData?.weekoff_category?.fixed_amount ?? 0,
          weekoff_attendance_payment_factor:
            getData?.weekoff_category?.week_off_payment_factor ?? 0,
          holiday_attendance_fixed_amount:
            getData?.holiday_category?.fixed_amount ?? 0,
          holiday_attendance_payment_factor:
            getData?.holiday_category?.holiday_payment_factor ?? 0,
          //absence
          absent_payment_based_on:
            getData?.absent_earnings?.absent_payment_based_on ?? "Paid",
          partial_payment_factor:
            getData?.absent_earnings?.partial_payment_factor ?? 1,
          //Weekoff configurations
          payment_for_week_off_attendance:
            getData?.weekoff_category?.[0]?.payment_for_week_off_attendance ??
            false,
          is_weekoff_payment_based_on:
            getData?.weekoff_category?.[0]?.payment_based_on ?? "Attendance",
          week_off_payment_factor:
            getData?.weekoff_category?.[0]?.week_off_payment_factor ?? 0,
          week_off_work_pre_authorization:
            getData?.weekoff_category?.[0]?.week_off_work_pre_authorization ??
            "Automatic",
          weekoff_calculation_based_on:
            getData?.weekoff_category?.[0]?.calculation_based_on ?? "Hours",
          is_weekoff_pay_by_default:
            getData?.weekoff_category[0]?.added_payroll_default,
          weekoff_thres_hold_limit:
            getData?.weekoff_category[0]
              ?.minimum_week_off_consideration_minutes ?? 0,
          weekoff_thres_hold_consideration:
            getData?.weekoff_category[0]?.minutes_for_consideration ?? 0,
          weekoff_additional_thres_hold:
            getData?.weekoff_category[0]
              ?.additional_week_off_consideration_minutes ?? 0,
          weekoff_additional_thres_hold_consideration:
            getData?.weekoff_category[0]
              ?.additional_minutes_for_consideration ?? 0,
          weekoff_payment_factor_configurations:
            getData?.weekoff_category?.[0]?.payment_factor_configurations ??
            getData?.weekoff_category?.[0]?.work_bands ??
            [],
          is_allow_payment_factor_for_weekoff:
            getData?.weekoff_category?.[0]?.attendance_payment_factor ?? false,
          is_add_remaining_weekoff_to_base_factor:
            getData?.weekoff_category?.[0]?.add_remaining_minutes_to_payroll ??
            false,
          is_weekoff_time_bound_break_deduction_allowed:
            getData?.weekoff_category?.[0]?.is_time_bount_break_deductions ??
            false,
          weekoff_time_bound_break_deduction_configurations:
            mapTimeBoundBreakRulesFromApi(
              getData?.weekoff_category?.[0]?.weekoff_time_boundary_rules,
            ),
          //amout
          weekoff_fixed_amount_thres_hold:
            getData?.weekoff_category[0]?.week_off_amount_payable ?? 0,
          weekoff_fixed_amount_thres_hold_consideration:
            getData?.weekoff_category[0]
              ?.threshold_week_off_consideration_minutes ?? 0,
          weekoff_fixed_amount_additional_thres_hold:
            getData?.weekoff_category[0]?.additional_week_off_amount_payable ??
            0,
          weekoff_fixed_amount_additional_thres_hold_consideration:
            getData?.weekoff_category[0]
              ?.additional_threshold_week_off_consideration_minutes ?? 0,
          weekoff_partial_shift_additional_percentage:
            getData?.weekoff_category[0]?.weekoff_compensation_rules?.[0]
              ?.additional_percentage ?? 0,
          weekoff_partial_shift_count:
            getData?.weekoff_category[0]?.weekoff_compensation_rules?.[0]
              ?.shift_per_day ?? 0,
          //weekoffRows:
          //Holiday configurations
          is_payment_for_holiday_day_attendance:
            getData?.holiday_category?.[0]?.payment_for_holiday_attendance ??
            false,
          is_holiday_payment_based_on:
            getData?.holiday_category?.[0]?.payment_based_on ?? "Attendance",
          holiday_sft_payment_factor:
            getData?.holiday_category?.[0]?.holiday_payment_factor ?? 0,
          holiday_work_preauthorization:
            getData?.holiday_category?.[0]?.holiday_work_pre_authorization ??
            "Automatic",
          holiday_calculation_based_on:
            getData?.holiday_category?.[0]?.calculation_based_on ?? "Hours",
          holiday_added_payroll_default:
            getData?.holiday_category[0]?.added_payroll_default,
          holiday_thres_hold_limit:
            getData?.holiday_category[0]
              ?.minimum_holiday_consideration_minutes ?? 0,
          holiday_thres_hold_consideration:
            getData?.holiday_category[0]?.minutes_for_consideration ?? 0,
          holiday_additional_thres_hold:
            getData?.holiday_category[0]
              ?.additional_holiday_consideration_minutes ?? 0,
          holiday_additional_thres_hold_consideration:
            getData?.holiday_category[0]
              ?.additional_minutes_for_consideration ?? 0,
          holiday_payment_factor_configurations:
            getData?.holiday_category?.[0]?.payment_factor_configurations ??
            getData?.holiday_category?.[0]?.work_bands ??
            [],
          is_allow_payment_factor_for_holiday:
            getData?.holiday_category?.[0]?.attendance_payment_factor ?? false,
          is_add_remaining_holiday_to_base_factor:
            getData?.holiday_category?.[0]?.add_remaining_minutes_to_payroll ??
            false,
          is_holiday_time_bound_break_deduction_allowed:
            getData?.holiday_category?.[0]?.is_time_bount_break_deductions ??
            false,
          holiday_time_bound_break_deduction_configurations:
            mapTimeBoundBreakRulesFromApi(
              getData?.holiday_category?.[0]?.holiday_time_boundary_rules,
            ),
          //amout
          holiday_fixed_amount_thres_hold:
            getData?.holiday_category[0]
              ?.threshold_holiday_consideration_minutes ?? 0,
          holiday_fixed_amount_payable:
            getData?.holiday_category[0]?.holiday_amount_payable ?? 0,
          holiday_fixed_amount_additional_mins:
            getData?.holiday_category[0]
              ?.additional_threshold_holiday_consideration_minutes ?? 0,
          holiday_fixed_amount_additional_payable:
            getData?.holiday_category[0]?.additional_holiday_amount_payable ??
            0,
          holiday_partial_shift_additional_percentage:
            getData?.holiday_category[0]?.holiday_compensation_rules?.[0]
              ?.additional_percentage ?? 0,
          holiday_partial_shift_count:
            getData?.holiday_category[0]?.holiday_compensation_rules?.[0]
              ?.shift_per_day ?? 0,
        },
        deductions: {
          deduct_salary_for_absence: getData?.deduct_salary_for_absence,
          consider_unpaid_leave_for_absent_penalty:
            getData?.consider_unpaid_leave_for_absent_penalty,
          is_absence_penalty_band_hours_paid:
            getData?.is_absence_penalty_band_hours_paid,
          is_leave_to_absent_convert:
            getData?.is_leave_to_absent_convert ?? false,
          is_leave_to_absent_convert_count:
            getData?.is_leave_to_absent_convert_count ?? 10,
          apply_penalty_count: getData?.apply_penalty_count ?? 1,
          is_apply_break_penalty_policy: getData?.break_penalty_deduction,
          is_apply_sft_penalty_policy: getData?.shift_penalty_deduction,
          is_absence_paid_notification: getData?.is_absence_paid,
          //sft penalty policy

          penalty_options:
            getData?.deduction_policies?.[0]?.penalty_based_on ?? "hours",
          dedctions_options:
            getData?.deduction_policies?.[0]?.deduction_based_on ??
            "Attendance",
          attendance_first_mins:
            getData?.deduction_policies?.[0]?.attendance_deductions?.[0]
              ?.first_initial ?? 0,
          attendance_late_exit:
            getData?.deduction_policies?.[0]?.attendance_deductions?.[0]
              ?.early_late ?? 0,

          deductions_attendance_deduct_type:
            getData?.deduction_policies?.[0]?.attendance_deductions?.[0]
              ?.deduction_type ?? "",

          attendance_initial_mins:
            getData?.deduction_policies?.[0]?.attendance_deductions?.[0]
              ?.after_initial ?? 0,

          attendance_additional_mins:
            getData?.deduction_policies?.[0]?.attendance_deductions?.[0]
              ?.additional_early_late ?? 0,

          fixed_amount_early_late:
            getData?.deduction_policies?.[0]?.fixed_amount_deductions?.[0]
              ?.fixed_amount_early_late ?? 0,

          fixed_amount_deduct_amount:
            getData?.deduction_policies?.[0]?.fixed_amount_deductions?.[0]
              ?.fixed_amount_deduction ?? 0,

          deductions_fixed_amount_deduct_type:
            getData?.deduction_policies?.[0]?.fixed_amount_deductions?.[0]
              ?.deduction_type ?? "",

          fixed_amount_extra_mins:
            getData?.deduction_policies?.[0]?.fixed_amount_deductions?.[0]
              ?.fixed_amount_after_initial ?? 0,

          fixed_amount_early_late_deduct_amt:
            getData?.deduction_policies?.[0]?.fixed_amount_deductions?.[0]
              ?.fixed_amount_additional_deduction ?? 0,

          sft_penalty_early_late_mins:
            getData?.deduction_policies?.[0]?.shift_based_deductions?.[0]
              ?.shift_early_late ?? 0,

          sft_penalty_deduct:
            getData?.deduction_policies?.[0]?.shift_based_deductions?.[0]
              ?.shift_deduction ?? 0,

          sft_penalty_additional_mins:
            getData?.deduction_policies?.[0]?.shift_based_deductions?.[0]
              ?.shift_after_initial ?? 0,

          sft_penalty_sft_deduct:
            getData?.deduction_policies?.[0]?.shift_based_deductions?.[0]
              ?.shift_additional_deduction ?? 0,

          sft_penalty_count_thershold:
            getData?.deduction_policies?.[0]?.count_based_deductions?.[0]
              ?.count_early_late ?? 0,

          sft_penalty_count_deduct:
            getData?.deduction_policies?.[0]?.count_based_deductions?.[0]
              ?.count_deduction ?? 0,

          sft_penalty_count_additional_mins:
            getData?.deduction_policies?.[0]?.count_based_deductions?.[0]
              ?.count_after_initial ?? 0,

          sft_penalty_count_sft_deduct:
            getData?.deduction_policies?.[0]?.count_based_deductions?.[0]
              ?.count_additional_deduction ?? 0,

          //absence deductions
          deduction: getData?.absent_deductions?.deduction ?? 0,
          additional_deduction:
            getData?.absent_deductions?.additional_deduction ?? 0,
          extra_deduction: getData?.absent_deductions?.extra_deduction ?? 0,
          extra_additional_deduction:
            getData?.absent_deductions?.extra_additional_deduction ?? 0,
          // break deductions
          break_penalty_deduct_type:
            getData?.break_penalty_policies?.[0]?.consideration ?? "",
          extra_break_minutes:
            getData?.break_penalty_policies?.[0]?.extra_break_minutes ?? 0,
          break_deduction:
            getData?.break_penalty_policies?.[0]?.break_deduction ?? 0,
          additional_break_minutes:
            getData?.break_penalty_policies?.[0]?.additional_break_minutes ?? 0,
          additional_break_deduction:
            getData?.break_penalty_policies?.[0]?.additional_break_deduction ??
            0,
          is_deduct_break_penalty_policy: getData?.break_penalty_policies[0]
            ?.deficit_working_hours
            ? "deficit"
            : "always",
        },
      });
    }
  }, [editCategoryDetails?.id, editCategoryDetails?.name, getData]);

  const handleStatus = async (status) => {
    // Notify parent about task start
    if (onTaskStart) {
      onTaskStart({
        categoryName: getData?.name,
        message: `${status === "Active" ? "Activating" : "Inactivating"} shift category...`,
        taskType: "status_update",
      });
    }

    const response = await customAPI(
      `${domain}administration/shiftroster/shiftrota/shift-categories/${getData?.id}/`,
      "PUT",
      {
        status: status,
      },
    );

    if (response.ok) {
      showToast(
        "success",
        `Shift category successfully ${status === "Active" ? "Activated" : "Inactivated"}`,
      );

      // Notify parent of completion
      if (onTaskComplete) {
        setTimeout(() => {
          onTaskComplete();
          reCallApi(true);
          onClose();
        }, 1000);
      }
    } else {
      // If failed, notify completion anyway
      if (onTaskComplete) {
        onTaskComplete();
      }
    }
  };

  // ADD: Function to handle task responses
  const handleTaskResponse = (result) => {
    if (result.task_id && result.websocket_url) {
      // Notify parent about task start
      if (onTaskStart) {
        onTaskStart({
          categoryName: editCategoryDetails?.name,
          message: "Starting background processing...",
          taskId: result.task_id,
          taskType: "update",
        });
      }

      // Close the side panel immediately
      setTimeout(() => {
        onClose();
      }, 500);

      // Connect WebSocket for progress updates
      connectTaskWebSocket(result.task_id, result.websocket_url);
    } else {
      // No background task - complete immediately
      if (onTaskComplete) {
        onTaskComplete();
        reCallApi(true);
        onClose();
      }
    }
  };

  // ADD: WebSocket connection function
  const connectTaskWebSocket = (taskId, websocketUrl) => {
    const socket = new WebSocket(websocketUrl);

    socket.onopen = () => {
      console.log("WebSocket connected for task:", taskId);
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("WebSocket progress update:", data);

        // Update parent with progress
        if (onTaskUpdate) {
          onTaskUpdate({
            progress: data.progress || 0,
            message: data.message || "",
            status: data.status || "PROGRESS",
          });
        }

        if (data.status === "SUCCESS") {
          showToast("success", "Background processing completed.");

          // Notify parent of completion
          setTimeout(() => {
            if (onTaskComplete) {
              onTaskComplete();
              reCallApi(true);
            }
            socket.close();
          }, 2000);
        } else if (data.status === "FAILURE") {
          showToast(
            "error",
            `Background processing failed: ${data.error || "Unknown error"}`,
          );
          if (onTaskComplete) {
            onTaskComplete();
          }
          socket.close();
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      showToast("warning", "Connection to progress updates interrupted");
    };

    socket.onclose = () => {
      console.log("WebSocket closed");
    };
  };

  return watch("loading") ? (
    <Loader />
  ) : (
    <>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ ml: 1 }}>
                  <IconButton
                    onClick={onClose}
                    sx={{
                      color: themes?.grayColor,
                      "&:hover": {
                        backgroundColor: themes?.whiteColor,
                      },
                    }}
                  >
                    <DoubleArrow />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "50%",
                    padding: "4px 4px",
                  }}
                >
                  <Tooltip title="Clone" arrow slotProps={tooltipStyleProps}>
                    <IconButton
                      onClick={() => {
                        if (screenAccess) {
                          handleCloneShiftPanel();
                        } else {
                          showToast(
                            "error",
                            `Access denied: You are not authorized to clone shift category`,
                          );
                        }
                      }}
                      sx={{
                        color: themes?.blueColor,
                        backgroundColor: themes?.primary,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: themes?.primary },
                      }}
                      disabled={getData?.status === "Inactive"}
                    >
                      <FileCopy />
                    </IconButton>
                  </Tooltip>
                  &nbsp;
                  <Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
                    <IconButton
                      onClick={() => {
                        if (screenAccess) {
                          handleEditShift();
                        } else {
                          showToast(
                            "error",
                            `Access denied: You are not authorized to edit shift category`,
                          );
                        }
                      }}
                      sx={{
                        color: themes?.greenColor,
                        backgroundColor: themes?.primary,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: themes?.primary },
                      }}
                      disabled={getData?.status === "Inactive"}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>{" "}
                  &nbsp;
                  {getData?.status === "Active" ? (
                    <Tooltip
                      title="Inactive"
                      arrow
                      slotProps={tooltipStyleProps}
                    >
                      <IconButton
                        onClick={() => {
                          if (screenAccess) {
                            handleStatus("Inactive");
                          } else {
                            showToast(
                              "error",
                              `Access denied: You are not authorized to inactivate shift category`,
                            );
                          }
                        }}
                        sx={{
                          color: themes?.redColor,
                          backgroundColor: themes?.primary,
                          borderRadius: "50%",
                          "&:hover": { backgroundColor: themes?.primary },
                        }}
                      >
                        <PowerSettingsNew />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Active" arrow slotProps={tooltipStyleProps}>
                      <IconButton
                        onClick={() => {
                          if (screenAccess) {
                            handleStatus("Active");
                          } else {
                            showToast(
                              "error",
                              `Access denied: You are not authorized to activate shift category`,
                            );
                          }
                        }}
                        sx={{
                          color: themes?.greenColor,
                          backgroundColor: themes?.primary,
                          borderRadius: "50%",
                          "&:hover": { backgroundColor: themes?.primary },
                        }}
                      >
                        <MotionPhotosAuto />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>

            {!isEmpty(formData?.basic) &&
              !isEmpty(formData?.earning) &&
              !isEmpty(formData?.deductions) &&
              !isEmpty(formData?.adjustment) && (
                <Box>
                  <Preview
                    basicInfoData={formData.basic}
                    earningData={formData.earning}
                    deductionsData={formData.deductions}
                    adjustmentData={formData?.adjustment}
                    isPreview={true}
                    onTaskResponse={handleTaskResponse}
                    editCategoryDetails={editCategoryDetails}
                    reCallApi={reCallApi}
                    onClose={onClose}
                  />
                </Box>
              )}
          </Box>
        </Paper>
      </Box>
      <Drawer
        anchor="right"
        open={openSidePeak}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          !openCloneSft ? handleEditClosePanel() : handleCloseClonePanel();
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "680px",
            maxWidth: "90vw",
            backgroundColor: themes?.whiteColor,
          },
        }}
      >
        {!openCloneSft ? (
          <EditShiftCategory
            basicInfoData={formData?.basic}
            earingData={formData?.earning}
            deductionsData={formData?.deductions}
            adjustmentData={formData?.adjustment}
            complianceData={complianceData}
            closeEditScreen={handleEditClosePanel}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
            setViewScreenData={setViewScreenData}
            setIsViewPanelOpen={setIsViewPanelOpen}
          />
        ) : (
          <CloneShiftCategory
            basicInfoData={{
              ...formData?.basic,
              sft_category_name: "",
            }}
            earingData={formData?.earning}
            deductionsData={formData?.deductions}
            adjustmentData={formData?.adjustment}
            complianceData={complianceData}
            closeCloneScreen={handleCloseClonePanel}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
            setViewScreenData={setViewScreenData}
            onClose={onClose}
          />
        )}
      </Drawer>
    </>
  );
};
